import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore, retimeShotToVideo, retimeTransitionToVideo } from '@/store/projectStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { acceptEnhancement } from '@/store/actions.ts';
import { capabilities } from '@/capabilities.ts';
import { FALLBACK_CATALOG, recordGenerationTime } from '@/ai/tokenCosts.ts';
import { useCountdown } from '@/hooks/useCountdown.ts';
import { AnnotationCanvas, MultiAnnotationOverlay } from './AnnotationCanvas.tsx';
import { ShotMovementOverlay } from './SceneBoard/ShotMovementOverlay.tsx';
import { shotMovementToNative } from '@/previs/converters.ts';
import { EnhancePreview } from './EnhancePreview.tsx';
// TtsPopover removed — h-menu Speech button now opens the full Audio Workspace
import { useCommentStore } from '@/store/commentStore.ts';
import type { TimelineShot } from '@/types/timeline.ts';
import type { ContinuousChainPair, TransitionAtTime } from '@/store/projectStore.ts';
import { describeAiError } from '@/ai/aiErrorMessages.ts';
import styles from './Viewer.module.css';

import { AiConfirmPopover } from './ViewerOverlayConfirm.tsx';
import type { ConfirmOpts } from './ViewerOverlayConfirm.tsx';
import { DoodleDialog, PostDrawPrompt } from './ViewerOverlayDoodles.tsx';
import { SpinnerIcon } from './ViewerOverlayTransitions.tsx';

// Re-exports so Viewer.tsx import stays unchanged
export { TransitionActions, GenerateTransitionBtn } from './ViewerOverlayTransitions.tsx';

interface ViewerOverlayProps {
  shot: TimelineShot;
  contentRect: { x: number; y: number; width: number; height: number };
  hasContentRect: boolean;
  activePair: ContinuousChainPair | null;
  transInfo: TransitionAtTime | null;
  unresolvedDoodleSvgs: string[];
  canDraw: boolean;
  reviewerColor: string;
  onFullscreen: () => void;
  onPathsChange: (paths: { d: string; stroke: string; strokeWidth: number }[]) => void;
  initialPenPoint?: { clientX: number; clientY: number } | null;
}

export function ViewerOverlay({
  shot, contentRect, hasContentRect, activePair, transInfo,
  unresolvedDoodleSvgs, canDraw, reviewerColor, onFullscreen, onPathsChange, initialPenPoint,
}: ViewerOverlayProps) {
  const { t } = useTranslation('viewer');
  const drawingMode = useUiStore(s => s.drawingMode);
  const readOnly = useProjectStore(s => s.readOnly);
  const activeEnhancementPreview = useAiStore(s => s.activeEnhancementPreview);
  // Derive preview URLs from generation history (preview stores only references)
  const previewEnhancedUrl = useAiStore(s => {
    const p = s.activeEnhancementPreview;
    if (!p) return '';
    return s.generationHistory.find(h => h.id === p.historyId)?.objectUrl ?? '';
  });
  const generatingImageForShot = useAiStore(s => s.generatingImageForShot);
  const generatingVideoForShot = useAiStore(s => s.generatingVideoForShot);
  const transitionVideoGenerating = useAiStore(s => s.transitionVideoGenerating);
  const aiSelectedImageModel = useAiStore(s => s.aiSelectedImageModel);
  const aiSelectedVideoModel = useAiStore(s => s.aiSelectedVideoModel);
  const aiModelCatalog = useAiStore(s => s.aiModelCatalog);

  // Store-driven quick confirm + TTS
  const quickConfirmMode = useUiStore(s => s.quickConfirmMode);
  const clearQuickConfirmMode = useUiStore(s => s.clearQuickConfirmMode);

  // Recording state from store
  const isRecording = useUiStore(s => s.isRecording);
  const startRecordingFn = useUiStore(s => s.startRecordingFn);
  const stopRecordingFn = useUiStore(s => s.stopRecordingFn);

  // Doodle dialog + post-draw prompt
  const doodleDialogOpen = useUiStore(s => s.doodleDialogOpen);
  const doodleDialogShotId = useUiStore(s => s.doodleDialogShotId);
  const closeDoodleDialog = useUiStore(s => s.closeDoodleDialog);
  const postDrawPromptOpen = useUiStore(s => s.postDrawPromptOpen);
  const postDrawPromptShotId = useUiStore(s => s.postDrawPromptShotId);
  const isCreator = useProjectStore(s => s.isCreator);

  const isEnhancing = generatingImageForShot === shot.id;
  const isGeneratingVideo = generatingVideoForShot === shot.id;

  const [editMenuClicked, setEditMenuClicked] = useState(false);
  const editMenuExpanded = editMenuClicked || drawingMode || isRecording;

  const editMenuRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);

  const effectiveCatalog = aiModelCatalog.length > 0 ? aiModelCatalog : FALLBACK_CATALOG;
  const enhanceCountdown = useCountdown(isEnhancing, aiSelectedImageModel, effectiveCatalog);
  const videoCountdown = useCountdown(isGeneratingVideo, aiSelectedVideoModel, effectiveCatalog);
  const transVideoCountdown = useCountdown(!!transitionVideoGenerating, aiSelectedVideoModel, effectiveCatalog);

  const showPreview = activeEnhancementPreview?.shotId === shot.id;
  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const hasDrawBtn = canDraw;
  const hasRecordBtn = !readOnly && !!startRecordingFn;
  const hasTtsBtn = capabilities.aiGeneration && !readOnly;
  const editBtnCount = (hasDrawBtn ? 1 : 0) + (hasRecordBtn ? 1 : 0) + (hasTtsBtn ? 1 : 0);
  const hasAnyEditAction = editBtnCount > 0;

  // Close edit menu + TTS popover + confirm popover on shot change
  const prevShotIdRef = useRef(shot.id);
  useEffect(() => {
    if (shot.id !== prevShotIdRef.current) {
      prevShotIdRef.current = shot.id;
      setEditMenuClicked(false);
      if (quickConfirmMode) clearQuickConfirmMode();
    }
  }, [shot.id, quickConfirmMode, clearQuickConfirmMode]);

  // Close clicked state on outside tap (for touch devices)
  useEffect(() => {
    if (!editMenuClicked && !quickConfirmMode) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (editMenuRef.current?.contains(target)) return;
      if (confirmRef.current?.contains(target)) return;
      setEditMenuClicked(false);
      if (quickConfirmMode) clearQuickConfirmMode();
    };
    window.addEventListener('pointerdown', handler, true);
    return () => window.removeEventListener('pointerdown', handler, true);
  }, [editMenuClicked, quickConfirmMode, clearQuickConfirmMode]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handleEnhance = useCallback(async (opts: ConfirmOpts) => {
    clearQuickConfirmMode();
    const aiState = useAiStore.getState();
    const ps = useProjectStore.getState();
    const proj = ps.project;
    if (!proj || !shot.thumbnailDataUrl) return;

    if (shot.historyId && !window.confirm(t('confirmReEnhance'))) return;

    // Warn if this shot is part of a continuous transition — enhancing the
    // thumbnail will break visual continuity with the transition video.
    const transitions = proj.transitions ?? [];
    const inContinuous = transitions.some(
      t => t.type === 'continuous' && t.videoObjectUrl && (t.fromShotId === shot.id || t.toShotId === shot.id),
    );
    if (inContinuous && !window.confirm(t('confirmContinuousBreak'))) return;

    aiState.setGeneratingImage(shot.id);
    const startTime = Date.now();

    try {
      const { enhanceSingleShot } = await import('@/ai/aiOrchestrator.ts');
      const scene = proj.scenes.find(s => s.shots.some(sh => sh.id === shot.id));
      if (!scene) throw new Error('Scene not found');

      const result = await enhanceSingleShot(
        shot,
        { name: scene.name, locationType: scene.locationType, timeOfDay: scene.timeOfDay },
        null, undefined, undefined, undefined,
        { modelId: aiSelectedImageModel, customBasePrompt: opts.customPrompt },
      );

      recordGenerationTime(aiSelectedImageModel, (Date.now() - startTime) / 1000);

      aiState.setEnhancementPreview({
        shotId: shot.id,
        prompt: result.prompt,
        tokenCost: result.tokenCost,
        seed: result.seed,
        historyId: result.historyId!,
      });

      useAccountStore.getState().refreshBalance();
    } catch (err) {
      console.error('Enhancement failed:', err);
      useUiStore.getState().showAiError('Enhancement Stopped', describeAiError(err));
    } finally {
      useAiStore.getState().setGeneratingImage(null);
    }
  }, [shot, aiSelectedImageModel, clearQuickConfirmMode, t]);

  const handleVideoGenerate = useCallback(async (opts: ConfirmOpts) => {
    clearQuickConfirmMode();
    const ps = useProjectStore.getState();
    const proj = ps.project;
    if (!proj) return;

    // Presence guard only — the generation SOURCE is not read here. generateSingleVideo takes the
    // shot and resolves its own input (ai/generationInput.ts fetches the original pixels for an
    // embedded static shot, whose thumbnailDataUrl is only Unity's cache thumbnail).
    if (!shot.thumbnailDataUrl) return;

    // Confirm replacement if shot already has a video
    if (shot.videoObjectUrl && !window.confirm(t('confirmReplaceVideo'))) return;

    useAiStore.getState().setGeneratingVideo(shot.id);
    const startTime = Date.now();

    try {
      const { generateSingleVideo } = await import('@/ai/videoOrchestrator.ts');
      const scene = proj.scenes.find(s => s.shots.some(sh => sh.id === shot.id));
      if (!scene) throw new Error('Scene not found');

      const result = await generateSingleVideo(
        shot,
        { name: scene.name, locationType: scene.locationType, timeOfDay: scene.timeOfDay },
        undefined, undefined,
        {
          modelId: aiSelectedVideoModel,
          customVideoPrompt: opts.customPrompt,
          generateAudio: opts.generateAudio,
          audioHint: opts.audioHint,
        },
      );

      recordGenerationTime(aiSelectedVideoModel, (Date.now() - startTime) / 1000);
      ps.attachVideo(shot.id, result.videoUrl, 'video/mp4', result.historyId);

      if (useAiStore.getState().retimeToMatchVideo && result.videoUrl) {
        await retimeShotToVideo(shot.id, result.videoUrl);
      }

      /* success — no toast */
      useAccountStore.getState().refreshBalance();
    } catch (err) {
      console.error('Video generation failed:', err);
      useUiStore.getState().showAiError('Video Generation Stopped', describeAiError(err));
    } finally {
      useAiStore.getState().setGeneratingVideo(null);
    }
  }, [shot, aiSelectedVideoModel, clearQuickConfirmMode, t]);

  const handleTransitionGenerate = useCallback(async (opts: ConfirmOpts) => {
    clearQuickConfirmMode();
    const ps = useProjectStore.getState();
    const ai = useAiStore.getState();
    if (ai.transitionVideoGenerating) return;

    const info = (transInfo && transInfo.transition.type === 'continuous')
      ? transInfo
      : activePair;
    if (!info) return;

    const fromShotId = info.fromShot.id;
    const toShotId = info.toShot.id;

    const allShotsArr = ps.allShots();
    const fShot = allShotsArr.find(sh => sh.id === fromShotId);
    const tShot = allShotsArr.find(sh => sh.id === toShotId);
    if (!fShot || !tShot) return;

    const transition = ps.getTransition(fromShotId, toShotId);
    if (!transition || transition.type !== 'continuous') return;

    const sceneInfo = ps.getSceneForShot(fromShotId);
    if (!sceneInfo) return;

    useAiStore.setState({ transitionVideoGenerating: { fromShotId, toShotId } });

    try {
      const { generateTransitionVideo } = await import('@/ai/transitionVideoOrchestrator.ts');
      const result = await generateTransitionVideo(
        transition, fShot, tShot, sceneInfo,
        undefined, undefined,
        { modelId: ai.aiSelectedVideoModel, customPrompt: opts.customPrompt },
      );
      useProjectStore.getState().attachTransitionVideo(fromShotId, toShotId, result.videoUrl, 'video/mp4', result.historyId);
      if (useAiStore.getState().retimeToMatchVideo) {
        await retimeTransitionToVideo(fromShotId, toShotId, result.videoUrl);
      }
      /* success — no toast */
      useAccountStore.getState().refreshBalance();
    } catch (err) {
      console.error('Transition video generation failed:', err);
      useUiStore.getState().showAiError('Transition Generation Stopped', describeAiError(err));
    } finally {
      useAiStore.setState({ transitionVideoGenerating: null });
    }
  }, [transInfo, activePair, clearQuickConfirmMode]);

  const handleConfirm = useCallback((opts: ConfirmOpts) => {
    if (quickConfirmMode === 'enhance') handleEnhance(opts);
    else if (quickConfirmMode === 'video') handleVideoGenerate(opts);
    else if (quickConfirmMode === 'transition') handleTransitionGenerate(opts);
  }, [quickConfirmMode, handleEnhance, handleVideoGenerate, handleTransitionGenerate]);

  const handleAcceptEnhancement = useCallback(() => {
    const preview = useAiStore.getState().activeEnhancementPreview;
    if (preview) {
      acceptEnhancement(preview.shotId);
      /* success — no toast */
    }
  }, []);

  const handleDismissEnhancement = useCallback(() => {
    useAiStore.getState().rejectEnhancement();
  }, []);

  const handleDrawToggle = useCallback(() => {
    const ui = useUiStore.getState();

    // If already drawing, toggle off (normal exit → post-draw prompt)
    if (ui.drawingMode) {
      ui.toggleDrawingMode();
      return;
    }

    // Entering drawing mode — check for existing doodles
    const cs = useCommentStore.getState();
    const myName = cs.reviewerName;
    const doodleComments = cs.comments.filter(
      c => c.shotId === shot.id && c.annotationSvg
    );
    const myDoodle = doodleComments.find(c => c.author === myName);
    const otherDoodles = doodleComments.filter(c => c.author !== myName);

    // Creator with others' doodles → creator dialog
    if (isCreator && otherDoodles.length > 0) {
      ui.openDoodleDialog(shot.id);
      return;
    }

    // Reviewer with own existing doodle → reviewer dialog
    if (myDoodle) {
      ui.openDoodleDialog(shot.id);
      return;
    }

    // No existing doodles → straight into drawing mode
    ui.toggleDrawingMode();
  }, [shot.id, isCreator]);

  const handleRecordToggle = useCallback(() => {
    if (isRecording && stopRecordingFn) {
      stopRecordingFn();
    } else if (!isRecording && startRecordingFn) {
      // Viewer overlay recordings always go to the Voiceover lane (3)
      useUiStore.getState().setRecordingLane(3);
      startRecordingFn();
    }
  }, [isRecording, startRecordingFn, stopRecordingFn]);

  // ── Render ──────────────────────────────────────────────────────

  // Derive fromShot/toShot for transition confirm popover
  const transitionPairInfo = (transInfo && transInfo.transition.type === 'continuous')
    ? transInfo
    : activePair;

  // contentRect IS the letterboxed shot frame — Viewer builds it from the project's aspect ratio
  // (ViewerContent.computeContentRect at 1000 × 1000/projectAR), so its own ratio is the project's.
  // Handing the movement overlay the same number for geometry AND container keeps its inner frame
  // at 100%, i.e. exactly this rect, with no second letterboxing. 16:9 only for a degenerate rect,
  // which matches the overlay's own fallback.
  const contentAspect = contentRect.height > 0 ? contentRect.width / contentRect.height : 16 / 9;

  return (
    <>
      {/* Full-screen modal overlay during instant video generation */}
      {isGeneratingVideo && (
        <div className={styles.videoGenModal}>
          <div className={styles.videoGenModalContent}>
            <SpinnerIcon size={32} />
            <span className={styles.videoGenModalTitle}>{t('generatingVideoTitle')}</span>
            <span className={styles.videoGenModalTimer}>
              {videoCountdown > 0 ? t('generatingVideoTimeRemaining', { seconds: videoCountdown }) : t('generatingVideoWorking')}
            </span>
            <span className={styles.videoGenModalHint}>{t('generatingVideoHint')}</span>
          </div>
        </div>
      )}

      {/* Centered generating overlay for continuous transition */}
      {!!transitionVideoGenerating && (
        <button
          className={styles.generateTransBtn}
          disabled
          style={{ top: '50%', bottom: 'auto', transform: 'translate(-50%, -50%)' }}
        >
          <SpinnerIcon size={16} />
          {transVideoCountdown > 0 ? `${transVideoCountdown}s` : t('generatingTransitionFallback')}
        </button>
      )}

      {/* Content-rect-relative overlays */}
      {hasContentRect && (
        <>
          {/* Camera-movement arrows — the same composite Unity draws over its own shot lists and
              the three board surfaces mount. Framed on contentRect, the SAME rect the read-only
              doodle svg below uses, so the two annotation layers share one frame authority (and
              ride fullscreen for free: the wrapper grows with contentRect and the swiper's active
              slide is transparent). FIRST in this fragment so every control that follows paints
              above it. Informational, so it stays mounted in read-only/review sessions too. */}
          <span
            className={styles.movementFrame}
            style={{ left: contentRect.x, top: contentRect.y, width: contentRect.width, height: contentRect.height }}
          >
            <ShotMovementOverlay
              nativeMovement={shot.shotMovement ? shotMovementToNative(shot.shotMovement) : undefined}
              aspectRatio={contentAspect}
              containerAspect={contentAspect}
            />
          </span>

          {/* Zoom button — upper right of content rect */}
          {!showPreview && (
            <button
              className={styles.zoomBtn}
              style={{ left: contentRect.x + contentRect.width - 34, top: contentRect.y + 6 }}
              onClick={onFullscreen}
              title={t('fullscreenToggleTooltip')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15,3 21,3 21,9" /><polyline points="9,21 3,21 3,15" />
                <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          )}

          {/* Enhancing modal — full-screen blocking overlay (matches video gen) */}
          {isEnhancing && (
            <div className={styles.videoGenModal}>
              <div className={styles.videoGenModalContent}>
                <SpinnerIcon size={32} />
                <span className={styles.videoGenModalTitle}>{t('enhancingImageTitle')}</span>
                <span className={styles.videoGenModalTimer}>
                  {enhanceCountdown > 0 ? t('generatingVideoTimeRemaining', { seconds: enhanceCountdown }) : t('generatingVideoWorking')}
                </span>
                <span className={styles.videoGenModalHint}>{t('enhancingImageHint')}</span>
              </div>
            </div>
          )}

          {/* Edit Tools Menu — bottom of content rect */}
          {!showPreview && hasAnyEditAction && editBtnCount === 1 ? (
            /* Single button — show directly without expandable trigger */
            <div
              ref={editMenuRef}
              className={styles.editMenu}
              data-orient="viewer-markup"
              style={{
                left: contentRect.x + 6,
                top: contentRect.y + contentRect.height - 34,
                width: contentRect.width - 12,
              }}
            >
              <div className={`${styles.editMenuItems} ${styles.editMenuItemsOpen}`}>
                {hasDrawBtn && (
                  <button
                    className={`${styles.editMenuBtn} ${drawingMode ? styles.editMenuBtnActive : ''}`}
                    onClick={handleDrawToggle}
                    disabled={isPlaying}
                    style={isPlaying ? { opacity: 0.35 } : undefined}
                    title={t('drawAnnotationTooltip')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  </button>
                )}
                {hasRecordBtn && (
                  <button
                    className={`${styles.editMenuBtn} ${isRecording ? styles.editMenuBtnRecording : ''}`}
                    onClick={handleRecordToggle}
                    title={isRecording ? t('stopRecordingTooltip') : t('recordAudioTooltip')}
                  >
                    {isRecording ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="6" width="12" height="12" rx="2" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                    )}
                  </button>
                )}
                {hasTtsBtn && (
                  <button
                    className={styles.editMenuBtn}
                    onClick={() => { useUiStore.getState().setScratchScope('custom'); useUiStore.getState().openAudioWorkspace('tts'); }}
                    title={t('addSpeechTooltip')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <polygon points="10,8 10,14 15,11" fill="currentColor" stroke="none" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ) : !showPreview && hasAnyEditAction && (
            /* Multiple buttons — expandable menu with trigger */
            <div
              ref={editMenuRef}
              className={styles.editMenu}
              data-orient="viewer-markup"
              style={{
                left: contentRect.x + 6,
                top: contentRect.y + contentRect.height - 34,
                width: contentRect.width - 12,
              }}
            >
              {/* Expanded items — reveal right-to-left */}
              <div className={`${styles.editMenuItems} ${editMenuExpanded ? styles.editMenuItemsOpen : ''}`}>
                {/* Draw — leftmost */}
                {hasDrawBtn && (
                  <button
                    className={`${styles.editMenuBtn} ${drawingMode ? styles.editMenuBtnActive : ''}`}
                    onClick={handleDrawToggle}
                    disabled={isPlaying}
                    style={isPlaying ? { opacity: 0.35 } : undefined}
                    title={t('drawAnnotationTooltip')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  </button>
                )}

                {/* Record audio */}
                {hasRecordBtn && (
                  <button
                    className={`${styles.editMenuBtn} ${isRecording ? styles.editMenuBtnRecording : ''}`}
                    onClick={handleRecordToggle}
                    title={isRecording ? t('stopRecordingTooltip') : t('recordAudioTooltip')}
                  >
                    {isRecording ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="6" width="12" height="12" rx="2" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                    )}
                  </button>
                )}

                {/* Comment */}
                {hasTtsBtn && (
                  <button
                    className={styles.editMenuBtn}
                    onClick={() => { useUiStore.getState().setScratchScope('custom'); useUiStore.getState().openAudioWorkspace('tts'); }}
                    title={t('addSpeechTooltip')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <polygon points="10,8 10,14 15,11" fill="currentColor" stroke="none" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Ellipsis trigger — always rightmost */}
              <button
                className={`${styles.editMenuTrigger} ${editMenuExpanded ? styles.editMenuTriggerOpen : ''} ${(drawingMode || isRecording) ? styles.editMenuTriggerActive : ''}`}
                onClick={() => {
                  if (editMenuExpanded) {
                    setEditMenuClicked(false);
                    if (drawingMode) useUiStore.getState().toggleDrawingMode();
                    if (isRecording && stopRecordingFn) stopRecordingFn();
                  } else {
                    setEditMenuClicked(true);
                  }
                }}
                title={t('editToolsTooltip')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="8" cy="12" r="1.25" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="12" r="1.25" fill="currentColor" stroke="none" />
                  <circle cx="16" cy="12" r="1.25" fill="currentColor" stroke="none" />
                </svg>
              </button>
            </div>
          )}

          {/* Unified AI confirmation popover + backdrop (store-driven) */}
          {quickConfirmMode && (
            <>
              <div className={styles.singleShotBackdrop} onClick={() => clearQuickConfirmMode()} />
              <AiConfirmPopover
                ref={confirmRef}
                mode={quickConfirmMode}
                shot={shot}
                fromShot={transitionPairInfo?.fromShot}
                toShot={transitionPairInfo?.toShot}
                catalog={effectiveCatalog}
                onConfirm={handleConfirm}
                onCancel={() => clearQuickConfirmMode()}
              />
            </>
          )}

          {/* Enhancement preview */}
          {showPreview && activeEnhancementPreview && (
            <EnhancePreview
              originalDataUrl={shot.thumbnailDataUrl ?? ''}
              enhancedDataUrl={previewEnhancedUrl}
              prompt={activeEnhancementPreview.prompt}
              onAccept={handleAcceptEnhancement}
              onRetry={(_editedPrompt: string) => {
                useAiStore.getState().setEnhancementPreview(null);
                useUiStore.getState().openAiEnhanceDialog();
              }}
              onDismiss={handleDismissEnhancement}
            />
          )}

          {/* Drawing canvas */}
          {drawingMode && canDraw && (
            <AnnotationCanvas contentRect={contentRect} onPathsChange={onPathsChange} strokeColor={reviewerColor} onFinish={() => useUiStore.getState().setDrawingMode(false)} initialPoint={initialPenPoint} />
          )}

          {/* Multi-user read-only annotation overlay */}
          {unresolvedDoodleSvgs.length > 0 && !drawingMode && (
            <MultiAnnotationOverlay svgJsonList={unresolvedDoodleSvgs} contentRect={contentRect} />
          )}

          {/* Doodle management dialog */}
          {doodleDialogOpen && doodleDialogShotId === shot.id && (
            <>
              <div className={styles.singleShotBackdrop} onClick={closeDoodleDialog} />
              <DoodleDialog
                shotId={shot.id}
                isCreator={isCreator}
                onClose={closeDoodleDialog}
              />
            </>
          )}

          {/* Post-draw comment prompt */}
          {postDrawPromptOpen && postDrawPromptShotId === shot.id && (
            <>
              <div className={styles.singleShotBackdrop} onClick={() => useUiStore.getState().closePostDrawPrompt()} />
              <PostDrawPrompt shotId={shot.id} />
            </>
          )}
        </>
      )}
    </>
  );
}
